/**
 * Корзина.
 *
 * Убранное лежит здесь неделю и только потом удаляется по-настоящему —
 * вместе со снимками, записями голоса и отчётами. Неделя нужна ровно для
 * одного: «убрал не тот» — ошибка, которую совершают одной рукой у стенда,
 * и заметить её можно через день.
 *
 * Список живёт на сервере: корзина общая для всех, кто работает с каталогом,
 * и держать её копию на телефоне незачем.
 */
import { useEffect, useState } from 'react';
import { apiFetch, syncNow } from '../sync';
import { plural } from '@core';

interface TrashStone {
  id: string; public_no: string | null; local_no: string | null;
  kind: string; removed_at: string; company_name: string | null; has_order: boolean;
}
interface TrashSupplier { id: string; company_name: string; removed_at: string; merged_into_name?: string | null }
interface TrashTalk { id: string; started_at: string; removed_at: string; lines: number; company_name: string | null }

export default function Trash({ onBack }: { onBack: () => void }) {
  const [days, setDays] = useState(7);
  const [stones, setStones] = useState<TrashStone[]>([]);
  const [suppliers, setSuppliers] = useState<TrashSupplier[]>([]);
  const [talks, setTalks] = useState<TrashTalk[]>([]);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');

  async function load() {
    setProblem('');
    try {
      const res = await apiFetch('/api/trash');
      if (!res.ok) throw new Error(String(res.status));
      const d = (await res.json()) as { days: number; stones: TrashStone[]; suppliers: TrashSupplier[]; talks?: TrashTalk[] };
      setDays(d.days);
      setStones(d.stones);
      setSuppliers(d.suppliers);
      setTalks(d.talks ?? []);
    } catch {
      setProblem('Корзина живёт на сервере — без связи её не показать.');
    }
  }
  useEffect(() => { void load(); }, []);

  async function act(kind: 'stone' | 'supplier' | 'talk', id: string, what: 'restore' | 'purge') {
    setBusy(id);
    try {
      const res = await apiFetch(`/api/trash/${kind}/${id}/${what}`, { method: 'POST' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setProblem((d as any)?.error ?? 'Не получилось.');
      } else if (what === 'restore') {
        // Возвращённое приедет на устройство ближайшей синхронизацией.
        void syncNow();
      }
      await load();
    } finally {
      setBusy('');
    }
  }

  /** Сколько осталось до окончательного удаления — понятными словами. */
  function left(removedAt: string): string {
    const gone = (Date.now() - new Date(removedAt).getTime()) / 86_400_000;
    const rest = Math.ceil(days - gone);
    if (rest <= 0) return 'удаляется на днях';
    return `${rest} ${plural(rest, 'день', 'дня', 'дней')} до удаления`;
  }

  const empty = !problem && stones.length === 0 && suppliers.length === 0 && talks.length === 0;

  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Ещё</button>
        <h1 className="t">Корзина</h1>
        <p className="meta">
          Убранное лежит здесь {days} {plural(days, 'день', 'дня', 'дней')}, потом удаляется
          совсем — вместе со снимками и записями.
        </p>
      </div>

      <div className="scroll">
        {problem && <div className="memo warn">{problem}</div>}

        {empty && (
          <div className="blankslate">
            <b>Пусто</b>
            Сюда попадает то, что вы убрали из каталога. Неделю это можно вернуть.
          </div>
        )}

        {stones.length > 0 && (
          <>
            <div className="head"><h3>Камни</h3><span>{stones.length}</span></div>
            <div className="list">
              {stones.map((s) => (
                <div className="row" key={s.id}>
                  <span className="grow">
                    <span className="nm">{s.public_no ?? s.local_no ?? s.id}</span>
                    <span className="sub">
                      {[s.company_name, left(s.removed_at)].filter(Boolean).join(' · ')}
                      {s.has_order && ' · есть заявка покупателя'}
                    </span>
                  </span>
                  <button className="btn-quiet" disabled={busy === s.id}
                          onClick={() => void act('stone', s.id, 'restore')}>Вернуть</button>
                  <button className="btn-quiet" disabled={busy === s.id}
                          onClick={() => {
                            if (window.confirm(`Удалить ${s.public_no ?? s.local_no} насовсем?\n\nСнимки, записи голоса и отчёты этого камня исчезнут. Вернуть будет нечего.`)) {
                              void act('stone', s.id, 'purge');
                            }
                          }}>Удалить</button>
                </div>
              ))}
            </div>
          </>
        )}

        {suppliers.length > 0 && (
          <>
            <div className="head"><h3>Стенды</h3><span>{suppliers.length}</span></div>
            <div className="list">
              {suppliers.map((s) => (
                <div className="row" key={s.id}>
                  <span className="grow">
                    <span className="nm">{s.company_name}</span>
                    <span className="sub">
                      {s.merged_into_name ? `влит в «${s.merged_into_name}» · ` : ''}{left(s.removed_at)}
                    </span>
                  </span>
                  {/* Влитый стенд пуст — возвращать нечего, всё его содержимое уже там. */}
                  {!s.merged_into_name && (
                    <button className="btn-quiet" disabled={busy === s.id}
                            onClick={() => void act('supplier', s.id, 'restore')}>Вернуть</button>
                  )}
                  <button className="btn-quiet" disabled={busy === s.id}
                          onClick={() => {
                            if (window.confirm(`Удалить стенд «${s.company_name}» насовсем?`)) {
                              void act('supplier', s.id, 'purge');
                            }
                          }}>Удалить</button>
                </div>
              ))}
            </div>
          </>
        )}

        {talks.length > 0 && (
          <>
            <div className="head"><h3>Беседы</h3><span>{talks.length}</span></div>
            <div className="list">
              {talks.map((t) => (
                <div className="row" key={t.id}>
                  <span className="grow">
                    <span className="nm">{t.company_name ?? 'Без стенда'}</span>
                    <span className="sub">
                      {new Date(t.started_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {' · '}{t.lines} {plural(t.lines, 'реплика', 'реплики', 'реплик')} · {left(t.removed_at)}
                    </span>
                  </span>
                  <button className="btn-quiet" disabled={busy === t.id}
                          onClick={() => void act('talk', t.id, 'restore')}>Вернуть</button>
                  <button className="btn-quiet" disabled={busy === t.id}
                          onClick={() => {
                            if (window.confirm('Удалить беседу насовсем?')) void act('talk', t.id, 'purge');
                          }}>Удалить</button>
                </div>
              ))}
            </div>
          </>
        )}

        {stones.some((s) => s.has_order) && (
          <div className="memo">
            Камень, по которому пришла заявка покупателя, сам не удалится: заявка
            ссылалась бы в пустоту. Он останется здесь, пока вы не удалите его вручную.
          </div>
        )}
      </div>
    </div>
  );
}
